/**
 * Derive Stables monitoring KPIs from live wallets/transactions.
 */

import { isBaasStableWalletAsset } from "../../../../Services/baasStablesWalletApi";

const STATUS_KEYS = ["active", "pending", "blocked", "inactive"];

const resolveTxAmount = (tx) => {
  const candidates = [
    tx?.amountStable,
    tx?.amount,
    tx?.amountFrom,
    tx?.amountTo,
    tx?.netToAmount,
    tx?.fromAmount,
  ];
  for (const value of candidates) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const resolveTxAssets = (tx) => {
  const assets = new Set();
  const push = (value) => {
    const asset = String(value || "").trim().toUpperCase();
    if (asset && isBaasStableWalletAsset(asset)) assets.add(asset);
  };
  push(tx?.asset);
  push(tx?.from);
  push(tx?.to);
  push(tx?.fromAssetId);
  push(tx?.toAssetId);
  push(tx?.source?.asset);
  push(tx?.destination?.asset);
  if (String(tx?.operation || "").toLowerCase() === "swap") {
    push(tx?.from);
    push(tx?.to);
  }
  return [...assets];
};

const isProcessedStatus = (status) => {
  const value = String(status || "").toLowerCase();
  return value === "processed" || value === "completed" || value === "concluida";
};

const isFailedStatus = (status) => {
  const value = String(status || "").toLowerCase();
  return value.includes("fail") || value.includes("falh") || value.includes("cancel");
};

const isPendingStatus = (status) => {
  const value = String(status || "").toLowerCase();
  return (
    value.includes("process")
    || value.includes("pend")
    || value.includes("allocat")
    || value === "em_processamento"
  );
};

export const buildBaasStablesWalletHealth = (wallets = []) => {
  const byStatus = STATUS_KEYS.reduce((acc, key) => {
    acc[key] = 0;
    return acc;
  }, {});

  const byNetwork = {};
  const byAsset = {};
  let totalAvailable = 0;
  let totalBlocked = 0;

  wallets.forEach((wallet) => {
    const status = STATUS_KEYS.includes(wallet?.status) ? wallet.status : "inactive";
    byStatus[status] += 1;

    const network = wallet?.network || "—";
    byNetwork[network] = (byNetwork[network] ?? 0) + 1;

    // StableBalance assets only — never fold fiat BRL / AccountBalance into BRL1 metrics.
    const asset = String(wallet?.asset || "").trim().toUpperCase();
    if (!isBaasStableWalletAsset(asset)) return;

    if (!byAsset[asset]) {
      byAsset[asset] = {
        asset,
        count: 0,
        available: 0,
        blocked: 0,
        iconUrl: wallet?.iconUrl ?? null,
      };
    }
    byAsset[asset].count += 1;
    byAsset[asset].available += Number(wallet?.balances?.available ?? 0);
    byAsset[asset].blocked += Number(wallet?.balances?.blocked ?? 0);
    if (!byAsset[asset].iconUrl && wallet?.iconUrl) {
      byAsset[asset].iconUrl = wallet.iconUrl;
    }

    totalAvailable += Number(wallet?.balances?.available ?? 0);
    totalBlocked += Number(wallet?.balances?.blocked ?? 0);
  });

  return {
    total: wallets.length,
    byStatus,
    byNetwork: Object.entries(byNetwork)
      .map(([network, count]) => ({ network, count }))
      .sort((a, b) => b.count - a.count || a.network.localeCompare(b.network)),
    byAsset: Object.values(byAsset).sort((a, b) => a.asset.localeCompare(b.asset)),
    totalAvailable,
    totalBlocked,
  };
};

export const buildBaasStablesActivitySnapshot = (transactions = []) => {
  let processed = 0;
  let pending = 0;
  let failed = 0;
  const volumeByAsset = {};

  transactions.forEach((tx) => {
    const status = String(tx?.status || "").toLowerCase();
    if (isProcessedStatus(status)) processed += 1;
    else if (isFailedStatus(status)) failed += 1;
    else if (isPendingStatus(status) || status) pending += 1;

    // Count volume for completed + processing (exclude failed only).
    if (isFailedStatus(status)) return;

    const amount = resolveTxAmount(tx);
    if (amount <= 0) return;

    const assets = resolveTxAssets(tx);
    if (!assets.length) {
      const fallback = String(tx?.asset || "").trim().toUpperCase();
      if (fallback) {
        volumeByAsset[fallback] = (volumeByAsset[fallback] ?? 0) + amount;
      }
      return;
    }

    assets.forEach((asset) => {
      volumeByAsset[asset] = (volumeByAsset[asset] ?? 0) + amount;
    });
  });

  const total = transactions.length;
  const successRate = total > 0 ? (processed / total) * 100 : 100;

  return {
    total,
    processed,
    pending,
    failed,
    successRate,
    volumeByAsset,
    recent: transactions.slice(0, 8),
  };
};

/**
 * Rail volumes derived from live stables transactions (major units per asset).
 * Counts all txs; volumes include completed + processing.
 */
export const getBaasStablesRailMetrics = (transactions = []) => {
  const activity = buildBaasStablesActivitySnapshot(transactions);
  return {
    provisional: false,
    volumeByAsset: { ...activity.volumeByAsset },
    volumeUsdc: activity.volumeByAsset.USDC ?? 0,
    volumeUsdt: activity.volumeByAsset.USDT ?? 0,
    volumeBrl1: activity.volumeByAsset.BRL1 ?? 0,
    txCount: activity.total,
    successRate: activity.successRate,
    trendVolume: buildTrendVolumeSeries(transactions),
    assets: Object.keys(activity.volumeByAsset).sort(),
  };
};

/** Last 7 UTC days of total stable volume (major units) for sparkline. */
const buildTrendVolumeSeries = (transactions = []) => {
  const days = 7;
  const byDay = new Map();
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);

  for (let i = days - 1; i >= 0; i -= 1) {
    const day = new Date(today);
    day.setUTCDate(today.getUTCDate() - i);
    byDay.set(day.toISOString().slice(0, 10), 0);
  }

  (transactions || []).forEach((tx) => {
    const status = String(tx?.status || "").toLowerCase();
    if (isFailedStatus(status)) return;

    const raw = tx?.createdAt || tx?.processedAt || tx?.raw?.created_at || tx?.raw?.createdAt;
    if (!raw) return;
    const key = String(raw).slice(0, 10);
    if (!byDay.has(key)) return;

    const amount = resolveTxAmount(tx);
    if (amount <= 0) return;
    byDay.set(key, (byDay.get(key) ?? 0) + amount);
  });

  return [...byDay.values()];
};

/** Preferred display order for known stables; unknown assets append alphabetically. */
export const sortBaasStableAssets = (assets = []) => {
  const preferred = ["BRL1", "USDC", "USDT", "EURC", "BRZ"];
  const unique = [...new Set(
    (assets || [])
      .map((asset) => String(asset || "").trim().toUpperCase())
      .filter(Boolean),
  )];
  const rank = new Map(preferred.map((asset, index) => [asset, index]));
  return unique.sort((a, b) => {
    const ra = rank.has(a) ? rank.get(a) : preferred.length;
    const rb = rank.has(b) ? rank.get(b) : preferred.length;
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b);
  });
};
